import React, { useCallback, useEffect } from 'react';
import { Modal, Form, Switch, Radio, Slider, Button } from 'antd';
import { DEFAULT_GRID_SETTINGS, type GridSettings } from '../../types/types';

// Defines the structure of form values, where opacity is 0-100 for the slider.
interface GridSettingsFormValues {
  enabled: boolean;
  type: 'square' | 'hex';
  size: number;
  opacity: number; // Used as a value between 0 and 100 in the form
  offsetX: number;
  offsetY: number;
  showLabels: boolean;
  clipToImage: boolean;
  distancePerUnit: number;
  distanceUnit: string;
}

interface GridSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  activeMapId: number | null;
  gridSettings: Record<string, GridSettings>;
  onUpdateGridSettings: (mapId: number, settings: GridSettings) => void;
}

const GridSettingsModal: React.FC<GridSettingsModalProps> = ({
  isOpen,
  onClose,
  activeMapId,
  gridSettings: currentGridSettings,
  onUpdateGridSettings,
}) => {
  const [form] = Form.useForm<GridSettingsFormValues>();

  useEffect(() => {
    if (isOpen) {
      // When the modal opens, populate the form with the current settings.
      // The opacity slider works with 0-100, but the state stores 0-1, so we convert it.
      const settings: GridSettings = (activeMapId !== null && currentGridSettings?.[activeMapId]) ?? DEFAULT_GRID_SETTINGS;
      form.setFieldsValue({
        ...settings,
        opacity: settings.opacity * 100,
      });
    }
  }, [isOpen, currentGridSettings, activeMapId, form]);

  const updateGridSettings = useCallback((settings: GridSettings) => {
    if (activeMapId === null) return;
    onUpdateGridSettings(activeMapId, settings);

    // Notify legacy map code that it needs to re-render the grid.
    if (window.notifyGridSettingsObservers) {
      window.notifyGridSettingsObservers();
    }
  }, [activeMapId, onUpdateGridSettings]);

  const handleValuesChange = useCallback((_changedValues: Partial<GridSettingsFormValues>, allValues: GridSettingsFormValues) => {
    // This function is called whenever any form field changes.
    // We immediately update the global state.
    const newSettings = {
      ...allValues,
      opacity: allValues.opacity / 100, // Convert back to 0-1 for state
    };
    updateGridSettings(newSettings);
  }, [updateGridSettings]);

  const handleReset = useCallback(() => {
    updateGridSettings(DEFAULT_GRID_SETTINGS);
    form.setFieldsValue({
      ...DEFAULT_GRID_SETTINGS,
      opacity: DEFAULT_GRID_SETTINGS.opacity * 100,
    });
  }, [form, updateGridSettings]);

  return (
    <Modal
      title="Grid Settings"
      style={{ position: 'fixed', bottom: 20, left: 20, top: 'auto', margin: 0 }}
      open={isOpen}
      onCancel={onClose}
      footer={[
        <Button key="reset" onClick={handleReset}>
          Reset to Default
        </Button>,
        <Button key="close" type="primary" onClick={onClose}>
          Close
        </Button>,
      ]}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleValuesChange}
        autoComplete="off"
      >
        <Form.Item name="enabled" label="Enabled" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="type" label="Grid Type">
          <Radio.Group>
            <Radio.Button value="square">Square</Radio.Button>
            <Radio.Button value="hex">Hex</Radio.Button>
          </Radio.Group>
        </Form.Item>
        <Form.Item label="Size">
          <Form.Item name="size" noStyle>
            <Slider min={10} max={200} step={1} />
          </Form.Item>
        </Form.Item>
        <Form.Item label="Opacity">
          <Form.Item name="opacity" noStyle>
            <Slider min={0} max={100} step={5} />
          </Form.Item>
        </Form.Item>
        <Form.Item label="X Offset">
          <Form.Item name="offsetX" noStyle>
            <Slider min={-100} max={100} step={1} />
          </Form.Item>
        </Form.Item>
        <Form.Item label="Y Offset">
          <Form.Item name="offsetY" noStyle>
            <Slider min={-100} max={100} step={1} />
          </Form.Item>
        </Form.Item>
        <Form.Item name="showLabels" label="Show Labels" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="clipToImage" label="Clip to Image" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default GridSettingsModal;